import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, Sun, Moon, Briefcase, HeartPulse } from "lucide-react";
import { cn } from "@/lib/utils";

const CHIPS = {
  emotional_goals: [
    { k: "peace", en: "Peace", nl: "Rust" },
    { k: "clarity", en: "Clarity", nl: "Helderheid" },
    { k: "connection", en: "Connection", nl: "Verbinding" },
    { k: "recovery", en: "Recovery", nl: "Herstel" },
    { k: "focus", en: "Focus", nl: "Focus" },
    { k: "self-kindness", en: "Self-kindness", nl: "Mildheid voor mezelf" },
  ],
  struggles: [
    { k: "anxiety", en: "Anxiety", nl: "Angst" },
    { k: "burnout", en: "Burnout", nl: "Burn-out" },
    { k: "cravings", en: "Cravings", nl: "Verlangen" },
    { k: "sleep", en: "Sleep", nl: "Slaap" },
    { k: "loneliness", en: "Loneliness", nl: "Eenzaamheid" },
    { k: "grief", en: "Grief", nl: "Verlies" },
  ],
  support_preferences: [
    { k: "reflection", en: "Reflection", nl: "Reflectie", icon: Sparkles },
    { k: "warmth", en: "Warmth", nl: "Warmte", icon: HeartPulse },
    { k: "structure", en: "Structure", nl: "Structuur", icon: Briefcase },
    { k: "gentle", en: "Gentle nudges", nl: "Zachte prikkels", icon: Sun },
    { k: "quiet", en: "Quiet", nl: "Stilte", icon: Moon },
  ],
};

function ChipGroup({ items, value, onChange, lang, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const active = value.includes(it.k);
        return (
          <button
            key={it.k}
            type="button"
            onClick={() => onChange(active ? value.filter((v) => v !== it.k) : [...value, it.k])}
            data-testid={`${testIdPrefix}-${it.k}`}
            className={cn(
              "rounded-full px-3.5 py-1.5 text-sm border transition",
              active
                ? "bg-sage-500 text-white border-sage-500"
                : "bg-white border-border text-ink-700 hover:text-ink-900"
            )}
          >
            {lang === "nl" ? it.nl : it.en}
          </button>
        );
      })}
    </div>
  );
}

export default function Onboarding() {
  const t = useT();
  const nav = useNavigate();
  const { refresh, user } = useAuth();
  const [lang, setLang] = useState(user?.language || "en");
  const [step, setStep] = useState(0);
  const [namePref, setNamePref] = useState(user?.name?.split(" ")?.[0] || "");
  const [emotional_goals, setGoals] = useState([]);
  const [struggles, setStruggles] = useState([]);
  const [support_preferences, setSupport] = useState([]);
  const [sleep_schedule, setSleep] = useState("");
  const [work_shifts, setWork] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setLang(user?.language || "en"); }, [user]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding", {
        emotional_goals,
        struggles,
        support_preferences,
        sleep_schedule,
        work_shifts,
        name_pref: namePref,
      });
      toast.success(t("common.saved"));
      await refresh();
      nav("/app", { replace: true });
    } catch (e) {
      toast.error(errMsg(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const steps = [
    {
      title: t("client.onboardingTitle"),
      subtitle: t("client.onboardingSub"),
      body: (
        <div className="space-y-4">
          <div>
            <Label>{t("client.preferredName")}</Label>
            <Input data-testid="onboarding-name" value={namePref} onChange={(e) => setNamePref(e.target.value)} className="mt-1.5 h-11 rounded-xl bg-white" />
          </div>
        </div>
      ),
    },
    {
      title: t("client.emotionalGoals"),
      body: <ChipGroup items={CHIPS.emotional_goals} value={emotional_goals} onChange={setGoals} lang={lang} testIdPrefix="onboarding-goal" />,
    },
    {
      title: t("client.struggles"),
      body: <ChipGroup items={CHIPS.struggles} value={struggles} onChange={setStruggles} lang={lang} testIdPrefix="onboarding-struggle" />,
    },
    {
      title: t("client.supportPrefs"),
      body: <ChipGroup items={CHIPS.support_preferences} value={support_preferences} onChange={setSupport} lang={lang} testIdPrefix="onboarding-support" />,
    },
    {
      title: t("client.sleep") + " · " + t("client.workShifts"),
      body: (
        <div className="space-y-4">
          <div>
            <Label>{t("client.sleep")}</Label>
            <Input data-testid="onboarding-sleep" value={sleep_schedule} onChange={(e) => setSleep(e.target.value)} placeholder="e.g. 23:00 — 07:00" className="mt-1.5 h-11 rounded-xl bg-white" />
          </div>
          <div>
            <Label>{t("client.workShifts")}</Label>
            <Textarea data-testid="onboarding-shifts" value={work_shifts} onChange={(e) => setWork(e.target.value)} rows={3} className="mt-1.5 rounded-xl bg-white" />
          </div>
        </div>
      ),
    },
  ];

  const s = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-50 p-6">
      <div className="max-w-xl w-full">
        <PageHeader eyebrow={`${step + 1} / ${steps.length}`} title={s.title} subtitle={s.subtitle} />
        <div className="rounded-3xl bg-white shadow-card p-6 md:p-8 animate-fade-up" data-testid={`onboarding-step-${step}`}>
          {s.body}
        </div>
        <div className="mt-6 flex items-center justify-between">
          <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="onboarding-back" className="rounded-full h-11 px-5 border-border">
            {t("common.back")}
          </Button>
          {!isLast && (
            <Button onClick={() => setStep(step + 1)} data-testid="onboarding-next" className="rounded-full h-11 px-6 bg-sage-500 hover:bg-sage-600">
              {t("common.next")}
            </Button>
          )}
          {isLast && (
            <Button onClick={submit} disabled={busy} data-testid="onboarding-submit" className="rounded-full h-11 px-6 bg-sage-500 hover:bg-sage-600">
              {busy ? t("common.saving") : t("common.continue")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
